import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Folder as FolderIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listFolders, createFolder, deleteFolder } from "../lib/api";

export default function Saves() {
  const [folders, setFolders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    listFolders()
      .then(setFolders)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const f = await createFolder(name.trim());
      setFolders((fs) => [f, ...fs]);
      setName("");
      toast.success(`Folder "${f.name}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create folder");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await deleteFolder(id).catch(() => {});
    setFolders((fs) => fs.filter((f) => f.folder_id !== id));
  };

  return (
    <div data-testid="saves-page" className="max-w-5xl mx-auto py-16 px-6 w-full">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <p className="font-meta text-[10px] uppercase tracking-[0.35em] text-zinc-600 mb-3">Your Library</p>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-white mb-4">Saves</h1>
        <p className="font-meta text-xs text-zinc-500 mb-8" data-testid="folders-count">
          {folders.length} {folders.length === 1 ? "FOLDER" : "FOLDERS"}
        </p>
        <form onSubmit={create} className="mb-12 w-full max-w-md bg-white/5 border border-white/10 rounded-full px-5 py-2.5 flex items-center gap-3 focus-within:border-white/30 focus-within:bg-white/10 transition-colors duration-300">
          <Plus className="w-4 h-4 text-zinc-500 shrink-0" />
          <input
            data-testid="new-folder-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Create a new folder…"
            className="bg-transparent border-none text-white placeholder-zinc-500 focus:outline-none focus:ring-0 w-full font-ui text-sm"
            spellCheck={false}
          />
          <button
            data-testid="create-folder-button"
            type="submit"
            disabled={creating}
            className="shrink-0 font-meta text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors duration-200 disabled:opacity-40"
          >
            Create
          </button>
        </form>
      </motion.div>

      {loaded && folders.length === 0 && (
        <div data-testid="saves-empty" className="py-24 text-center border border-dashed border-white/10 rounded-2xl">
          <p className="font-heading text-2xl text-zinc-600">No folders yet.</p>
          <p className="mt-3 font-meta text-xs uppercase tracking-[0.25em] text-zinc-700">
            Create one above, then save any playing video into it
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {folders.map((f, i) => (
          <motion.div
            key={f.folder_id}
            data-testid={`folder-card-${f.folder_id}`}
            onClick={() => navigate(`/saves/${f.folder_id}`)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="group border border-white/10 rounded-2xl overflow-hidden bg-white/[0.02] hover:border-white/25 transition-colors duration-300 cursor-pointer"
          >
            <div className="aspect-video bg-zinc-900 relative">
              {f.covers.length > 0 ? (
                <img src={f.covers[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                  <FolderIcon className="w-10 h-10" strokeWidth={1} />
                </div>
              )}
              <button
                data-testid={`delete-folder-${f.folder_id}`}
                onClick={(e) => remove(e, f.folder_id)}
                aria-label="Delete folder"
                className="absolute top-3 right-3 p-2.5 rounded-full bg-black/60 text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 flex items-center justify-between">
              <h2 className="font-heading text-xl text-white truncate">{f.name}</h2>
              <span className="font-meta text-[10px] tracking-[0.2em] text-zinc-500 uppercase shrink-0 ml-3">
                {f.count} saved
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
